import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import type { OwnerStorageCheckResult } from '@/hooks/useOwnerGate';
import styles from './OwnerRecoveryScreen.module.css';

interface StorageUnreadableScreenProps {
  onCheckAgain: () => Promise<OwnerStorageCheckResult>;
}

type Announcement = 'none' | 'checking' | 'still-unreadable';

/**
 * Closed screen for a browser profile whose saved data could not be inspected
 * (owner-gate status `storage-unreadable`, rule #87). Nothing owner-bearing is
 * shown or mounted, and there is no way in. There is no encrypted backup here:
 * it reads the same storage. Check again repeats the gate's checks; it does
 * not repair the browser, and the copy never says it does.
 */
export default function StorageUnreadableScreen({ onCheckAgain }: StorageUnreadableScreenProps) {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [checking, setChecking] = useState(false);
  const [announcement, setAnnouncement] = useState<Announcement>('none');
  const [signingOut, setSigningOut] = useState(false);
  // Closed before the first await, so a second tap in the same moment is ignored.
  const checkingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function handleCheckAgain() {
    if (checkingRef.current || signingOut) return;
    checkingRef.current = true;
    setChecking(true);
    setAnnouncement('checking');
    void onCheckAgain().then((result) => {
      if (!mountedRef.current) return;
      checkingRef.current = false;
      setChecking(false);
      setAnnouncement(result === 'still-unreadable' ? 'still-unreadable' : 'none');
    });
  }

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut({ force: true });
    } catch {
      setSigningOut(false);
    }
  }

  return (
    <div className={styles.root}>
      <main className={styles.cluster}>
        <svg
          className={styles.mark}
          width="44"
          height="44"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <ellipse cx="12" cy="6" rx="7" ry="3" />
          <path d="M5 6v12c0 1.66 3.13 3 7 3s7-1.34 7-3V6" />
          <path d="M5 12c0 1.66 3.13 3 7 3s7-1.34 7-3" />
        </svg>
        <h1 className={styles.title}>{t('accountLoad.storageTitle')}</h1>
        <p className={styles.body}>{t('accountLoad.storageBody')}</p>
        <p className={styles.body}>{t('accountLoad.storageNextStep')}</p>
        <div className={styles.actions}>
          {/* aria-disabled, not disabled: focus stays on the control while it checks. */}
          <button
            className={styles.primaryButton}
            type="button"
            aria-disabled={checking || signingOut ? true : undefined}
            onClick={handleCheckAgain}
          >
            {t('accountLoad.storageCheckAgain')}
          </button>
          <button
            className={styles.quietButton}
            type="button"
            disabled={signingOut}
            onClick={handleSignOut}
          >
            {signingOut ? t('accountLoad.signingOut') : t('accountLoad.signOut')}
          </button>
        </div>
        <p className={styles.status} role="status" aria-live="polite">
          {announcement === 'checking'
            ? t('accountLoad.storageChecking')
            : announcement === 'still-unreadable'
              ? t('accountLoad.storageStillUnreadable')
              : ''}
        </p>
      </main>
    </div>
  );
}
